import {
  AddGroupMembershipRequest,
  GroupMembership,
  RemoveGroupMembershipRequest,
  useToast,
} from '@aslanpay/portal-common-lib';
import { useDisclosure } from '@chakra-ui/react';
import useGroups from 'app/hooks/useGroups';
import useFetchIndividuals from 'app/hooks/useFetchIndividuals';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { PrideEmployeeListModalProps } from 'app/components/Modals/PrideEmployeeListModal/PrideEmployeeListModal.types';
import useGroupsWithPot from 'app/hooks/useGroupsWithPot/useGroupsWithPot';
import { GroupWithPot } from 'app/hooks/useGroupsWithPot/types';
import { collectFailedMembers, FailedMember } from 'app/utils/groupMembership';

import convertIndividualsToGroupMembers from '../../pages/RewardAndRecognition/Prides/Prides.function';
import { PrideEmployeeInformation } from '../../pages/RewardAndRecognition/Prides/Prides.types';

const PAGE_SIZE = 20;

function useGroupWithPotEmployees(companyId: string | undefined) {
  const toast = useToast();

  const { isLoading, groupsWithPots, flatGroupsWithPots, flatGroups } = useGroupsWithPot(companyId);

  const [selectedGroup, setSelectedGroup] = useState<GroupWithPot | undefined>();
  const [addEmployeePage, setAddEmployeePage] = useState(0);
  const [regularMembersPage, setRegularMembersPage] = useState(0);
  const [failedMembers, setFailedMembers] = useState<FailedMember[]>([]);

  const {
    isOpen: isAddEmployee,
    onClose: onCloseAddEmployee,
    onOpen: onOpenAddEmployee,
  } = useDisclosure();
  const {
    isOpen: isListEmployeesOpen,
    onClose: onCloseListEmployeesModal,
    onOpen: onOpenListEmployees,
  } = useDisclosure();

  const onCloseListEmployees = useCallback(() => {
    onCloseListEmployeesModal();
    onCloseAddEmployee();
    setSelectedGroup(undefined);
  }, [onCloseListEmployeesModal, onCloseAddEmployee]);

  // ── Data fetching ────────────────────────────────────────────────────────────

  const { data: membershipPages, isLoading: isLoadingMemberships } =
    useGroups.useFetchGroupMemberships(
      companyId,
      selectedGroup?.id,
      { status: 'ACTIVE' },
      !!selectedGroup
    );

  const { data: individuals, isLoading: isLoadingIndividuals } = useFetchIndividuals({
    companyId: companyId ? [companyId] : [],
    enabled: !!isAddEmployee && !!companyId,
    page: 0,
    size: 999999,
    role: ['EMPLOYEE'],
  });

  const { data: rawMemberships, isLoading: isLoadingAllMemberships } =
    useGroups.useFetchCompanyGroupMemberships(companyId, isAddEmployee);

  const allGroupMemberships = useMemo(() => {
    const byGroupId = new Map<string, GroupMembership[]>();
    (rawMemberships?.list ?? []).forEach((m) => {
      const existing = byGroupId.get(m.groupId) ?? [];
      existing.push(m);
      byGroupId.set(m.groupId, existing);
    });
    return flatGroups.map((group) => ({
      group,
      members: byGroupId.get(group.id) ?? [],
    }));
  }, [rawMemberships, flatGroups]);

  // ── Derived lists ────────────────────────────────────────────────────────────

  const members = useMemo(
    () => membershipPages?.pages.flatMap((p) => p.list) ?? [],
    [membershipPages]
  );

  const regularMembers = useMemo(
    (): PrideEmployeeInformation[] =>
      members.map((m) => ({
        ...m,
        primaryGroup: m.isPrimaryGroup ? selectedGroup : undefined,
        isInSelectedGroup: true,
      })),
    [members, selectedGroup]
  );

  const allAddEmployeeMembers = useMemo(
    (): PrideEmployeeInformation[] =>
      convertIndividualsToGroupMembers({
        allGroupMemberships,
        selectedGroup,
        individuals,
        selectedGroupMembers: members,
      }),
    [individuals, selectedGroup, allGroupMemberships, members]
  );

  // ── Client-side pagination ───────────────────────────────────────────────────

  const regularMembersSlice = useMemo(
    () => regularMembers.slice(0, (regularMembersPage + 1) * PAGE_SIZE),
    [regularMembers, regularMembersPage]
  );

  const addEmployeeMembersSlice = useMemo(
    () => allAddEmployeeMembers.slice(0, (addEmployeePage + 1) * PAGE_SIZE),
    [allAddEmployeeMembers, addEmployeePage]
  );

  const onFetchNextRegularPage = useCallback(() => setRegularMembersPage((p) => p + 1), []);
  const onFetchNextAddEmployeePage = useCallback(() => setAddEmployeePage((p) => p + 1), []);

  // ── Mutations ────────────────────────────────────────────────────────────────

  const { mutate: addGroupMemberships, isPending: isAddingMembers } =
    useGroups.useAddGroupMemberships(companyId, selectedGroup?.id);

  const { mutate: removeGroupMemberships } = useGroups.useRemoveGroupMemberships(
    companyId,
    selectedGroup?.id
  );

  const handleOnRemoveEmployees = useCallback(
    (memberIds: RemoveGroupMembershipRequest) => {
      removeGroupMemberships(memberIds, {
        onSuccess: () => {
          toast({
            position: 'top-right',
            status: 'success',
            title: `${memberIds.length} employee(s) removed from ${selectedGroup?.name}`,
            description: `The selected employee(s) have been removed from this pride.`,
          });
        },
        onError: () => {
          toast({
            position: 'top-right',
            status: 'error',
            title: 'Error',
            description: `Failed to remove employees. Please try again.`,
          });
        },
      });
    },
    [removeGroupMemberships, selectedGroup, toast]
  );

  const handleOnAddEmployees = useCallback(
    (membersToAdd: AddGroupMembershipRequest) => {
      const nameById = new Map(
        allAddEmployeeMembers.map((m) => [m.individualId, m.individualName])
      );
      const total = membersToAdd.length;
      setFailedMembers([]);

      addGroupMemberships(membersToAdd, {
        onSuccess: (data) => {
          // The endpoint returns 200 even when some rows fail, so partial failures surface here, not onError.
          const failed = collectFailedMembers([data], (id) => nameById.get(id));

          if (failed.length === 0) {
            toast({
              position: 'top-right',
              status: 'success',
              title: `${total} employee(s) added to ${selectedGroup?.name}`,
              description: `The selected employee(s) now belong to this pride.`,
            });
            onCloseAddEmployee();
            return;
          }

          setFailedMembers(failed);
          const succeededCount = total - failed.length;
          toast({
            position: 'top-right',
            status: succeededCount === 0 ? 'error' : 'warning',
            title:
              succeededCount === 0
                ? 'No employees added'
                : `${succeededCount} of ${total} employee(s) added to ${selectedGroup?.name}`,
            description: `Some employees could not be added. See the reasons below.`,
          });
        },
        onError: () => {
          // A whole-request failure (network / 5xx) — none of the rows applied.
          setFailedMembers(
            membersToAdd.map((m) => ({
              id: m.individualId,
              name: nameById.get(m.individualId) ?? m.individualId,
              reason: 'This employee could not be added. Please try again.',
            }))
          );
          toast({
            position: 'top-right',
            status: 'error',
            title: 'Error',
            description: `Failed to add employees. Please try again.`,
          });
        },
      });
    },
    [addGroupMemberships, allAddEmployeeMembers, selectedGroup, toast, onCloseAddEmployee]
  );

  // ── Side effects ─────────────────────────────────────────────────────────────

  useEffect(() => {
    if (selectedGroup) onOpenListEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroup]);

  useEffect(() => {
    if (!isAddEmployee) setAddEmployeePage(0);
    setFailedMembers([]);
  }, [isAddEmployee]);

  useEffect(() => {
    setRegularMembersPage(0);
    setAddEmployeePage(0);
  }, [selectedGroup?.id]);

  // ── Modal props ──────────────────────────────────────────────────────────────

  const modalProps: PrideEmployeeListModalProps = {
    companyId,
    isOpen: isListEmployeesOpen,
    onClose: onCloseListEmployees,
    selectedGroup,
    isOpenAddEmployee: isAddEmployee,
    onOpenAddEmployee,
    onCloseAddEmployee,
    onAddEmployees: handleOnAddEmployees,
    onRemoveEmployees: handleOnRemoveEmployees,
    members: isAddEmployee ? addEmployeeMembersSlice : regularMembersSlice,
    allMembers: isAddEmployee ? allAddEmployeeMembers : regularMembers,
    isLoading: isAddEmployee
      ? isLoadingIndividuals || isLoadingAllMemberships || isLoadingMemberships
      : isLoadingMemberships,
    onFetchNextPage: isAddEmployee ? onFetchNextAddEmployeePage : onFetchNextRegularPage,
    hasNextPage: isAddEmployee
      ? allAddEmployeeMembers.length > (addEmployeePage + 1) * PAGE_SIZE
      : regularMembers.length > (regularMembersPage + 1) * PAGE_SIZE,
    isLoadingNextPage: false,
    failedMembers,
    isSubmitting: isAddingMembers,
  };

  return {
    isLoading,
    hasGroups: groupsWithPots.length > 0,
    groupsWithPots,
    flatGroupsWithPots,
    selectedGroup,
    setSelectedGroup,
    modalProps,
  };
}

export default useGroupWithPotEmployees;
